import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import { statusMeta } from "../campaignUtils";

export const QuestionIcon = () => (
  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>
);

let helpTipCount = 0;

// Small "?" that keeps explanations off the page. Hover or focus previews the
// points; a click pins them (for touch) until Escape or an outside click.
export const HelpTip = ({ points, label = "More information", align = "left" }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const rootRef = useRef(null);
  const idRef = useRef(null);
  if (!idRef.current) idRef.current = `cs-help-${++helpTipCount}`;
  const open = hovered || pinned;

  useEffect(() => {
    if (!open) return undefined;
    const handleKey = event => {
      if (event.key === "Escape") {
        setPinned(false);
        setHovered(false);
      }
    };
    const handlePointer = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setPinned(false);
    };
    document.addEventListener("keydown", handleKey);
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer);
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
    };
  }, [open]);

  const items = [].concat(points).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <span
      ref={rootRef}
      className={`cs-help cs-help--${align}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Inside a <label>, a click here must not toggle the input.
      onClick={event => event.preventDefault()}
    >
      <button
        type="button"
        className={`cs-help__button ${open ? "is-open" : ""}`}
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? idRef.current : undefined}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={() => setPinned(value => !value)}
      >
        <QuestionIcon />
      </button>
      {open && (
        <span id={idRef.current} role="tooltip" className="cs-help__panel">
          <span className="cs-help__card">
            <ul className="cs-help__list">
              {items.map((item, index) => <li key={index}>{item}</li>)}
            </ul>
          </span>
        </span>
      )}
    </span>
  );
};

// Explanation shown as short bullets instead of a paragraph.
export const Points = ({ items, className = "" }) => {
  const list = [].concat(items).filter(Boolean);
  if (list.length === 0) return null;
  return (
    <ul className={`cs-points ${className}`}>
      {list.map((item, index) => <li key={index}>{item}</li>)}
    </ul>
  );
};

export const Action = ({ children, variant = "secondary", className = "", type = "button", ...props }) => (
  <button type={type} className={`cs-button cs-button--${variant} ${className}`} {...props}>
    {children}
  </button>
);

export const StatusPill = ({ status, label }) => {
  const meta = statusMeta(status);
  return (
    <span className={`cs-pill cs-pill--${meta.tone}`}>
      <span aria-hidden="true" className="cs-pill__glyph">{meta.glyph}</span>
      {label || meta.label}
    </span>
  );
};

export const Chip = ({ children, className = "" }) => <span className={`cs-chip ${className}`}>{children}</span>;

export const Spinner = ({ label }) => (
  <span className="cs-spinner-wrap">
    <span className="cs-spinner" aria-hidden="true" />
    {label && <span>{label}</span>}
  </span>
);

export const Alert = ({ tone = "info", title, children, live = true }) => (
  <div
    className={`cs-alert cs-alert--${tone}`}
    role={live ? (tone === "danger" ? "alert" : "status") : undefined}
  >
    {title && <p className="cs-alert__title">{title}</p>}
    {children && <div className="cs-alert__body">{children}</div>}
  </div>
);

export const Skeleton = ({ heights = [96, 96, 96], label = "Loading" }) => (
  <div className="cs-skeleton-list" aria-busy="true" aria-label={label}>
    {heights.map((height, index) => (
      <div key={index} className="cs-skeleton" style={{ height }} />
    ))}
  </div>
);

export const EmptyState = ({ title, description, children }) => (
  <div className="cs-empty">
    <p className="cs-empty__title">{title}</p>
    {description && <p className="cs-muted">{description}</p>}
    {children && <div className="cs-empty__actions">{children}</div>}
  </div>
);

export const Card = ({ title, description, help, aside, children, className = "", labelledBy }) => (
  <section className={`cs-card ${className}`} aria-labelledby={labelledBy}>
    {(title || aside || help) && (
      <header className="cs-card__header">
        <div className="cs-card__heading">
          {title && <h2 id={labelledBy}>{title}</h2>}
          {description && <p className="cs-muted">{description}</p>}
        </div>
        {(aside || help) && (
          <div className="cs-card__aside">
            {aside}
            {help && <HelpTip points={help} label={title ? `About ${title}` : undefined} align="right" />}
          </div>
        )}
      </header>
    )}
    {children}
  </section>
);

export const Disclosure = ({ summary, children, defaultOpen = false }) => (
  <details className="cs-disclosure" open={defaultOpen}>
    <summary>{summary}</summary>
    <div className="cs-disclosure__body">{children}</div>
  </details>
);

export const Check = ({ children, hint, help, ...props }) => (
  <label className="cs-check">
    <input type="checkbox" {...props} />
    <span>
      {children}
      {help && <HelpTip points={help} />}
      {hint && <small className="cs-muted cs-block">{hint}</small>}
    </span>
  </label>
);

export const Radio = ({ children, hint, help, ...props }) => (
  <label className="cs-check">
    <input type="radio" {...props} />
    <span>
      {children}
      {help && <HelpTip points={help} />}
      {hint && <small className="cs-muted cs-block">{hint}</small>}
    </span>
  </label>
);

export const Field = ({ label, hint, help, error, children }) => {
  const id = `cs-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const describedBy = [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  return (
    <div className="cs-field">
      <span className="cs-field__label">
        <label htmlFor={id}>{label}</label>
        {help && <HelpTip points={help} label={`About ${label}`} />}
      </span>
      {React.Children.map(children, child =>
        React.isValidElement(child) && ["input", "select", "textarea"].includes(child.type)
          ? React.cloneElement(child, { id, "aria-describedby": child.props["aria-describedby"] || describedBy })
          : child
      )}
      {hint && <small id={`${id}-hint`} className="cs-muted">{hint}</small>}
      {error && <small id={`${id}-error`} className="cs-field__error">{error}</small>}
    </div>
  );
};

export function Pages({ data, onPage, disabled, label = "Pagination" }) {
  if (!data || data.last_page <= 1) return null;
  return (
    <nav className="cs-pages" aria-label={label}>
      <Action disabled={disabled || data.current_page <= 1} onClick={() => onPage(data.current_page - 1)}>Previous</Action>
      <span>Page {data.current_page} of {data.last_page}</span>
      <Action disabled={disabled || data.current_page >= data.last_page} onClick={() => onPage(data.current_page + 1)}>Next</Action>
    </nav>
  );
}

export function Tabs({ label, values, value, onChange, format = item => item }) {
  return (
    <div role="tablist" aria-label={label} className="cs-tabs">
      {values.map((item, index) => (
        <button
          type="button"
          role="tab"
          key={item}
          aria-selected={value === item}
          tabIndex={value === item ? 0 : -1}
          onClick={() => onChange(item)}
          onKeyDown={event => {
            let next;
            if (event.key === "ArrowRight") next = (index + 1) % values.length;
            if (event.key === "ArrowLeft") next = (index + values.length - 1) % values.length;
            if (event.key === "Home") next = 0;
            if (event.key === "End") next = values.length - 1;
            if (next !== undefined) {
              event.preventDefault();
              onChange(values[next]);
              event.currentTarget.parentNode.children[next].focus();
            }
          }}
        >
          {format(item)}
        </button>
      ))}
    </div>
  );
}

export const ConfirmModal = ({ confirm, isPending, onCancel, onConfirm }) => (
  <Modal show={!!confirm} onHide={isPending ? () => {} : onCancel} className="cs-modal">
    {confirm && (
      <React.Fragment>
        <Modal.Header closeButton={!isPending}>
          <Modal.Title>{confirm.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {Array.isArray(confirm.description)
            ? <ul className="cs-modal__text cs-modal__list">{confirm.description.map(item => <li key={item}>{item}</li>)}</ul>
            : <p className="cs-modal__text">{confirm.description}</p>}
        </Modal.Body>
        <Modal.Footer>
          <div className="cs-modal__actions">
            <Action disabled={isPending} onClick={onCancel}>{confirm.cancelLabel || "Cancel"}</Action>
            <Action variant={confirm.destructive ? "destructive" : "primary"} disabled={isPending} onClick={onConfirm}>
              {confirm.confirmLabel}
            </Action>
          </div>
        </Modal.Footer>
      </React.Fragment>
    )}
  </Modal>
);
